import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { IPageRoute, ISplitUrlLocation } from 'interfaces/routesInt';
import GenerateRoutes from '../generateRoute/GenerateRoutes';
import urls from '../urls';
import { usePage } from 'hooks/PlatformContext';
import LoadingComponent from 'components/shared/Loading';
import KycGate from 'components/bank/AGBBank/KycGate';
import AccountLockedPage from 'components/bank/AGBBank/AccountPending/AccountLockedPage';
import useAccountAccess, {
  ACCOUNT_LOCKED_PATHS,
} from 'hooks/useAccountAccess';

const formatUrl = (url: string) => url.slice(1);

const ManageRoutes: React.FC = () => {
  const { pathname } = useLocation();
  // Only signed-in users have linked accounts to check; this component also
  // renders the public auth pages.
  const hasSession = Boolean(localStorage.getItem('userToken'));
  const { isPending: isAccountPending } =
    useAccountAccess(hasSession);

  const splitLocation: ISplitUrlLocation = {
    segments: pathname.split('/'),
  };

  const LoginPage = usePage('LoginPage');
  const RegisterPage = usePage('RegistrationPage');
  const AccountOpeningPage = usePage('AccountOpeningPage');
  const DashboardPage = usePage('DashboardPage');
  const ForgotPasswordPage = usePage('ForgotPasswordPage');
  const RemindUsernamePage = usePage('RemindUsernamePage');
  const Transactions = usePage('Transactions');
  const Settings = usePage('Settings');
  const Accounts = usePage('Accounts');
  const LocalTransfer = usePage('LocalTransfer');
  const PayTax = usePage('PayTax');
  const WithdrawPage = usePage('WithdrawPage');
  const BeneficiaryPage = usePage('BeneficiaryPage');
  const PayBillsPage = usePage('PayBillsPage');
  const UserProfilePage = usePage('UserProfilePage');
  const MicroloanPage = usePage('MicroloanPage');
  const CardsPage = usePage('CardsPage');
  const TransferToMNOPage = usePage('TransferToMNOPage');
  const CashWithdrawPage = usePage('CashWithdrawPage');
  const OrganizationPage = usePage('OrganizationPage');
  const ChangePassowrdPage = usePage('ChangePasswordPage');
  const TellersPage = usePage('tellers');

  const pages: IPageRoute = {
    [formatUrl(urls.landingPage)]: LoginPage,
    [formatUrl(urls.login)]: LoginPage,
    [formatUrl(urls.register)]: RegisterPage,
    [formatUrl(urls.accountOpening)]: AccountOpeningPage,
    [formatUrl(urls.dashboard)]: DashboardPage,
    [formatUrl(urls.forgotPassword)]: ForgotPasswordPage,
    [formatUrl(urls.remindUsername)]: RemindUsernamePage,
    [formatUrl(urls.transactions)]: Transactions,
    [formatUrl(urls.settings)]: Settings,
    [formatUrl(urls.accounts)]: Accounts,
    [formatUrl(urls.localTransfer)]: LocalTransfer,
    [formatUrl(urls.payTax)]: PayTax,
    [formatUrl(urls.withdraw)]: WithdrawPage,
    [formatUrl(urls.beneficiary)]: BeneficiaryPage,
    [formatUrl(urls.payBills)]: PayBillsPage,
    [formatUrl(urls.userProfile)]: UserProfilePage,
    [formatUrl(urls.microloan)]: MicroloanPage,
    [formatUrl(urls.cards)]: CardsPage,
    [formatUrl(urls.operators)]: TransferToMNOPage,
    [formatUrl(urls.cashWithdraw)]: CashWithdrawPage,
    [formatUrl(urls.organization)]: OrganizationPage,
    [formatUrl(urls.changePassword)]: ChangePassowrdPage,
    [formatUrl(urls.tellers)]: TellersPage,
  };

  // Account-dependent pages show the "account being opened" page instead
  // while the user has no linked account yet.
  if (isAccountPending) {
    ACCOUNT_LOCKED_PATHS.forEach((path) => {
      pages[formatUrl(path)] = <AccountLockedPage />;
    });
  }

  return (
    <Suspense fallback={<LoadingComponent />}>
      <GenerateRoutes
        splitLocation={splitLocation}
        pageRoute={pages}
      />
      <KycGate />
    </Suspense>
  );
};

export default ManageRoutes;
